import $ from 'jquery';

import bputils from './bputils';

export default class Api {
  static apicalls = {};
  static SESSION_REFRESHING = false;
  // eslint-disable-next-line import/no-unresolved
  static env = require('../../build-static/env.json');

  static get(userOpts, success, error) {
    if (!userOpts.requestId) {
      throw Error('"requestId" must be passed for "get" requests.');
    }
    const opts = Object.assign({ contentType: undefined, data: undefined }, userOpts);
    return Api.processRequest('GET', opts, success, error);
  }

  static post(userOpts, success, error) {
    const opts = Object.assign(userOpts, { contentType: 'application/json', data: JSON.stringify(userOpts.data) });
    return Api.processRequest('POST', opts, success, error);
  }

  static postFile(userOpts, success, error) {
    const opts = Object.assign(userOpts, { data: userOpts.data, cache: false, contentType: false, processData: false, type: 'POST' });
    return Api.processRequest('POST', opts, success, error);
  }

  static put(userOpts, success, error) {
    const opts = Object.assign(userOpts, { contentType: 'application/json', data: JSON.stringify(userOpts.data) });
    return Api.processRequest('PUT', opts, success, error);
  }

  static patch(userOpts, success, error) {
    const opts = Object.assign(userOpts, { contentType: 'application/json', data: JSON.stringify(userOpts.data) });
    return Api.processRequest('PATCH', opts, success, error);
  }

  static delete(userOpts, success, error) {
    const opts = Object.assign({ contentType: undefined, data: undefined }, userOpts);
    return Api.processRequest('DELETE', opts, success, error);
  }

  static refreshSession(callback) {
    Api.regenerateSession(callback);
  }

  static abortRequests(requestIds) {
    const requestIdArray = [].concat(requestIds);
    requestIdArray.forEach((r) => {
      if (Api.apicalls[r]) {
        Api.apicalls[r].abort();
        Api.apicalls[r] = undefined;
      }
    });
  }

  static processRequest(method, userOpts, success, error) {
    const defaultOpts = {
      path: '',
      data: {},
      cache: false,
      success,
      error,
      complete: undefined,
      errorMessage: 'Error loading data',
      errorContainers: [],
    };

    let promise;
    if (typeof success !== 'function') {
      promise = new Promise((accept, reject) => {
        defaultOpts.success = accept;
        // Drive error off the same check to be consistent
        defaultOpts.error = reject;
      });
    }

    const opts = Object.assign({}, defaultOpts, userOpts);
    const headers = Api.getHeaders(opts.contentType);

    Api.abortRequests(opts.requestId);

    const request = {
      url: Api.buildURL(opts.path),
      method,
      data: opts.data,
      cache: opts.cache,
      headers,
      contentType: opts.contentType,
      processData: opts.processData,
      timeout: 1 * 60 * 1000, // timeout request after 60 seconds
      success: opts.success,
      complete: ((e) => {
        bputils.invokeCallback(opts.complete, e);
      }),
      error(e) {
        Api.handleAPIError(e, opts.error, request);
      },
    };

    const xhr = $.ajax(request);
    if (opts.requestId) {
      Api.apicalls[opts.requestId] = xhr;
    }
    return promise;
  }

  static getBpApi() {
    // eslint-disable-next-line import/no-unresolved
    return {
      key: Api.env.api_key,
      host: Api.env.api_host,
    };
  }

  static buildURL(path) {
    const bpApi = Api.getBpApi();
    return Api.appendQueryParam(bpApi.host + path, 'apikey', bpApi.key);
  }

  static appendQueryParam(url, key, value) {
    const isQuestionMarkPresent = url && url.indexOf('?') !== -1;
    const separator = isQuestionMarkPresent ? '&' : '?';
    return `${url}${separator}${key}=${value}`;
  }

  static getHeaders(contentType, opts = { auth: true }) {
    let headers = {};

    if (contentType) {
      headers = Object.assign({}, {
        'Content-Type': contentType,
      });
    }

    const accessToken = bputils.getAccessToken();
    if (opts.auth && accessToken) {
      headers = Object.assign({}, headers, {
        Authorization: `Bearer ${accessToken}`,
        token_type: bputils.store.get(),
        auth_context_id: bputils.store.get('auth_context_id'),
      });
    } else {
      const bpApi = Api.getBpApi();
      // When access_token is not available, at that time only apikey needs to be sent.
      headers.apikey = bpApi.key;
    }

    headers.app = bputils.getAppName();
    return headers;
  }

  static regenerateUserProfile() {
    Api.get({
      path: '/users/me',
      requestId: 'regenerateProfile',
      error() {
      },
      success(res) {
        bputils.store.set('username', res.first_name || res.last_name ? `${res.first_name} ${res.last_name}` : res.email);
        bputils.store.set('title', res.title);
        bputils.store.set('email', res.email);
        bputils.store.set('role', res.roles[0]);
        bputils.store.set('department', res.department);
        bputils.store.set('currentPageURL', window.location.href);
        bputils.store.set('permissions', res.permissions);
        window.location.reload();
      },
    });
  }

  static regenerateSession(callback) {
    const requestHeaders = Api.getHeaders('application/json', { auth: false });

    const bpApi = Api.getBpApi();
    requestHeaders.apikey = bpApi.key;

    const requestData = {
      email: bputils.store.get('email'),
      refresh_token: bputils.store.get('refresh_token'),
      auth_context_id: bputils.store.get('auth_context_id'),
      old_access_token: bputils.store.get('access_token'),
    };

    if (requestData.refresh_token && requestData.auth_context_id && requestData.old_access_token) {
      const request = {
        url: Api.buildURL('/session'),
        headers: Object.assign({}, requestHeaders),
        method: 'PATCH',
        data: JSON.stringify(requestData),
        cache: false,
        timeout: 1 * 60 * 1000, // timeout request after 60 seconds
        success(res) {
          // session regenerated; update values to be able to regenerate session again
          bputils.store.set('refresh_token', res.refresh_token);
          bputils.store.set('access_token', res.access_token);
          Api.regenerateUserProfile();
          // invoke callback if defined
          bputils.invokeCallback(callback);
        },
        error() {
          // session regeneration failed, log user out
          bputils.logOut();
        },
        complete() {
          Api.SESSION_REFRESHING = false;
        },
      };

      Api.SESSION_REFRESHING = true;
      $.ajax(request);
    } else {
      bputils.logOut();
    }
  }

  static regenerateUserCookies() {
    Api.get({
      path: '/users/me',
      requestId: 'regenerateCookies',
      error() {
      },
      success(res) {
        bputils.store.set('username', res.first_name || res.last_name ? `${res.first_name} ${res.last_name}` : res.email, 30);
        bputils.store.set('title', res.title, 30);
        bputils.store.set('email', res.email, 30);
        bputils.store.set('role', res.roles[0], 30);
        bputils.store.set('department', res.department, 30);
        bputils.store.set('currentPageURL', window.location.href, 30);
        bputils.store.set('permissions', JSON.stringify(res.permissions));
        window.location.reload();
      },
    });
  }

  // Deal with the error, either an expired session or a real error from the server
  static handleAPIError(err, errorHandlerOfCaller = () => null, failedRequest) {
    // If the status code was Unauthorized (401) or Forbidden (403), try to refresh the session
    // in production the status code is 0 for these because of CORS
    if ((err.status === 401 || err.status === 403 || err.status === 0)
      // don't refresh session if it was an aborted request, status will be 0 and status text will be 'error'
      // Forbidden and Unauthorized only happen in dev
      && (err.statusText === 'error' || err.statusText === 'Forbidden' || err.statusText === 'Unauthorized')
      // only care if user isn't on login page
      && !window.location.href.match(/\/login\.html/ig)) {
      if (!Api.SESSION_REFRESHING) {
        Api.regenerateSession();
      }
    } else if (err.statusText !== 'abort') {
      // Otherwise, this was perhaps a 404 or 500, handle it through the error callback
      // Display message and render standard error if error containers are known
      if (failedRequest.errorContainers && failedRequest.errorContainers.length > 0) {
        const errorMessage = failedRequest.errorMessage || 'Error loading data';
        toastr.error(errorMessage);
        bputils.showError(failedRequest.errorContainers);
      }

      bputils.invokeCallback(errorHandlerOfCaller, err);
    }
  }
}
